/**
 * Talents Service
 * Handles talent management including the special "Talented" talent that affects skills.
 *
 * All methods use string-based IDs exclusively (no numeric IDs).
 * Talents are identified by their kebab-case string `id` field.
 *
 * Requirements: 1.2, 1.5, 3.2, 4.2, 5.2
 */

import talentsData from '../data/talents.json';
import { Talent, TalentPurchase } from '../types/character';
import { createTalentPurchase } from '../utils/purchaseRecordHelpers';

/**
 * Result of calculating a talent's effects from character data.
 * Contains all components that contribute to the talent's effects.
 * All calculations use only character data - no external lookups required.
 *
 * Requirements: 2.1, 2.2, 3.5, 4.3, 9.3
 */
export interface TalentCalculation {
  /** The string ID of the talent being calculated */
  talentId: string;

  /** Display name of the talent */
  name: string;

  /**
   * Number of times this talent has been purchased.
   * Calculated from purchaseHistory.length.
   * For talents that can be purchased multiple times (like Weapon Training),
   * this represents the total number of purchases.
   */
  ranks: number;

  /**
   * Whether this talent is the "Talented" talent that affects skills.
   * When true, affectedSkillIds will contain the skills that receive +10 bonus.
   */
  isTalentedTalent: boolean;

  /**
   * For "Talented" talent - array of skill IDs that receive +10 bonus.
   * Each purchase of "Talented" can affect a different skill.
   * Empty array for non-Talented talents.
   */
  affectedSkillIds: string[];

  /**
   * The tier of the talent (1, 2, or 3).
   * Higher tiers indicate more powerful talents.
   */
  tier: number;

  /**
   * Whether this talent allows multiple purchases with different specializations.
   */
  allowsMultiplePurchases: boolean;

  /**
   * Specialization if applicable (e.g., "Las" for Weapon Training).
   */
  specialization?: string;

  /**
   * Source of the talent (e.g., "Career: Rogue Trader - Rank 2").
   */
  source?: string;
}

/**
 * Definition of a talent from the talents.json data file.
 * Uses string ID as the primary identifier - no numeric IDs.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.5, 5.2, 5.4
 */
export interface TalentDefinition {
  /**
   * String ID used as the primary identifier.
   * Uses kebab-case format for consistency.
   * @example "air-of-authority", "talented", "weapon-training-las"
   */
  id: string;

  /** Display name shown in the UI */
  name: string;

  /** Talent tier (1, 2, or 3) - indicates power level and XP cost category */
  tier: number;

  /** Array of prerequisite strings that must be met to acquire this talent */
  prerequisites: string[];

  /** Source rulebook */
  rulebook: string;

  /** Whether this talent has specializations */
  isSpecialized?: boolean;

  /** For specialized talents, the base talent name */
  baseTalent?: string;

  /** Specific specialization if applicable */
  specialization?: string;

  /**
   * Brief one-sentence summary suitable for hover tooltips.
   * Typically under 100 characters.
   * @example "Affect more targets with Command/Interaction Tests."
   */
  shortDescription: string;

  /**
   * Complete description of the talent including what it does and how it works.
   * Contains full rules text and mechanical benefits.
   * @example "The Explorer was born to command, either motivating or terrifying those under his charge..."
   */
  fullDescription: string;

  /**
   * Collection of specializations available for talents that can be taken multiple times.
   * Only present for talents like Weapon Training, Peer, Hatred, etc.
   * @example ["Bolt", "Las", "Melta", "Plasma"] for Weapon Training
   */
  talentGroups?: string[];
}

/**
 * The ID of the base "Talented" talent that can affect any skill.
 * This talent grants +10 to a chosen skill.
 */
const TALENTED_TALENT_ID = 'talented';

/**
 * IDs that indicate a "Talented" variant talent.
 * These all grant +10 to a specific skill.
 */

/** Pattern matching psy-rating-N talent IDs (levels 1-10) */
const PSY_RATING_PATTERN = /^psy-rating-(\d+)$/;

/**
 * Extract the level N from a psy-rating-N talent ID, or null if not a psy-rating talent.
 * Returns null for malformed IDs (e.g., psy-rating-0, psy-rating-11, psy-rating-abc).
 *
 * @param talentId - The talent ID to parse
 * @returns The psy rating level (1-10), or null if not a valid psy-rating talent
 *
 * Requirements: 3.1, 3.2
 */
export function parsePsyRatingLevel(talentId: string): number | null {
  const match = talentId.match(PSY_RATING_PATTERN);
  if (!match) return null;
  const level = parseInt(match[1], 10);
  if (level < 1 || level > 10) return null;
  return level;
}

/**
 * Check if a talent ID is a psy-rating-N talent (levels 1-10).
 *
 * @param talentId - The talent ID to check
 * @returns True if this is a valid psy-rating-N talent ID
 *
 * Requirements: 3.1, 3.2
 */
export function isPsyRatingTalent(talentId: string): boolean {
  return parsePsyRatingLevel(talentId) !== null;
}

/**
 * Derive the highest psy rating from a character's talent list.
 * Returns 0 if no psy-rating-N talents are present.
 *
 * @param talents - Array of objects with at least an `id` field
 * @returns The highest psy rating level (0 if none)
 *
 * Requirements: 3.1, 3.2
 */
export function getHighestPsyRating(talents: Array<{ id: string }>): number {
  let max = 0;
  for (const t of talents) {
    const level = parsePsyRatingLevel(t.id);
    if (level !== null && level > max) max = level;
  }
  return max;
}

export class TalentsService {
  /**
   * Get a talent definition by its string ID.
   *
   * @param id - The string ID of the talent (e.g., "air-of-authority", "talented")
   * @returns The talent definition or undefined if not found
   *
   * Requirements: 1.2, 1.5, 5.2
   */
  static getTalentById(id: string): TalentDefinition | undefined {
    return talentsData.talents.find((t) => t.id === id) as TalentDefinition | undefined;
  }

  /**
   * Check if a talent ID represents the "Talented" talent or a variant.
   * The "Talented" talent grants +10 to a chosen skill.
   *
   * @param talentId - The string ID of the talent to check
   * @returns True if this is a "Talented" talent variant
   *
   * Requirements: 4.2
   */
  static isTalentedTalent(talentId: string): boolean {
    // Check for exact match with base "talented" ID
    if (talentId === TALENTED_TALENT_ID) {
      return true;
    }

    // Check for "talented-" prefix (e.g., "talented-commerce", "talented-dodge")
    if (talentId.startsWith('talented-')) {
      return true;
    }

    // Also check the talent definition's baseTalent field
    const talentDef = this.getTalentById(talentId);
    if (talentDef?.baseTalent === 'Talented') {
      return true;
    }

    return false;
  }

  /**
   * Extract the affected skill ID from a "Talented" talent ID.
   * For talents like "talented-commerce", extracts "commerce".
   *
   * @param talentId - The string ID of the talented talent
   * @returns The skill ID or undefined if not a specialized talented talent
   */
  static extractSkillIdFromTalentedTalent(talentId: string): string | undefined {
    if (talentId.startsWith('talented-')) {
      return talentId.substring('talented-'.length);
    }

    // Check the talent definition's specialization field
    const talentDef = this.getTalentById(talentId);
    if (talentDef?.baseTalent === 'Talented' && talentDef.specialization) {
      // Convert specialization to skill ID format (kebab-case)
      return talentDef.specialization.toLowerCase().replace(/\s+/g, '-');
    }

    return undefined;
  }

  /**
   * Add a talent purchase to a character's talent list.
   * Creates the talent if it doesn't exist, or updates the existing talent's purchase history.
   *
   * For "Talented" talent, the affectedSkillId parameter is required to track
   * which skill receives the +10 bonus.
   *
   * @param characterTalents - The character's current talents array
   * @param talentId - The string ID of the talent (e.g., "air-of-authority", "talented")
   * @param advancementId - The unique ID of the advancement being purchased
   * @param rank - The career rank when this advancement was purchased
   * @param affectedSkillId - For "talented" talent - which skill gets +10 (required for talented)
   * @returns Updated talents array with the new purchase recorded, or original array if validation fails
   *
   * Requirements: 1.2, 1.5, 3.2, 4.1, 4.2, 4.4, 5.2
   */
  static addTalentPurchase(
    characterTalents: Talent[],
    talentId: string,
    advancementId: string,
    rank: number,
    affectedSkillId?: string
  ): Talent[] {
    // Find the talent definition
    const talentDef = this.getTalentById(talentId);
    if (!talentDef) {
      console.warn(`Talent not found: ${talentId}`);
      return characterTalents;
    }

    // Check if this is a "Talented" talent
    const isTalented = this.isTalentedTalent(talentId);

    // For "Talented" talent, validate and handle affectedSkillId
    if (isTalented) {
      // Try to extract from the talent ID itself if not provided
      if (!affectedSkillId) {
        const extractedSkillId = this.extractSkillIdFromTalentedTalent(talentId);
        if (extractedSkillId) {
          affectedSkillId = extractedSkillId;
        }
      }

      // Requirement 4.4: Validate that affectedSkillId is present for "Talented"
      if (!affectedSkillId) {
        console.error(`Talented talent requires affectedSkillId: ${talentId}`);
        return characterTalents; // Prevent purchase without skill selection
      }

      // Requirement 4.1: Prevent duplicate "Talented" for same skill
      const validation = this.validateTalentedPurchase(characterTalents, affectedSkillId);
      if (!validation.valid) {
        console.error(`Invalid Talented purchase: ${validation.error}`);
        return characterTalents; // Prevent duplicate purchase
      }
    }

    // Create the purchase record
    const purchase = createTalentPurchase(advancementId, rank, affectedSkillId);

    // Find existing talent in character's talents
    const existingTalentIndex = characterTalents.findIndex(
      (t) => t.id === talentId || t.name.toLowerCase() === talentDef.name.toLowerCase()
    );

    if (existingTalentIndex >= 0) {
      // Update existing talent
      const updatedTalents = [...characterTalents];
      const existingTalent = updatedTalents[existingTalentIndex];

      // Ensure purchaseHistory exists
      const purchaseHistory = existingTalent.purchaseHistory || [];

      updatedTalents[existingTalentIndex] = {
        ...existingTalent,
        id: talentId, // Ensure we're using the string ID
        purchaseHistory: [...purchaseHistory, purchase],
        purchaseCount: (existingTalent.purchaseCount || 0) + 1,
        // For single-purchase talented talents, store the affectedSkillId at the talent level
        ...(this.isTalentedTalent(talentId) &&
          affectedSkillId &&
          purchaseHistory.length === 0 && {
            affectedSkillId
          })
      };

      return updatedTalents;
    } else {
      // Create new talent with the purchase
      const newTalent: Talent = {
        id: talentId,
        name: talentDef.name,
        tier: talentDef.tier,
        prerequisites: talentDef.prerequisites,
        shortDescription: talentDef.shortDescription,
        fullDescription: talentDef.fullDescription,
        rulebook: talentDef.rulebook,
        baseTalent: talentDef.baseTalent,
        specialization: talentDef.specialization,
        isChoosePlaceholder: false,
        hasEnumeratedOptions: talentDef.isSpecialized,
        allowMultiplePurchases: talentDef.isSpecialized,
        purchaseCount: 1,
        purchaseHistory: [purchase],
        source: 'Advancement',
        // For "Talented" talent, store the affected skill ID
        ...(this.isTalentedTalent(talentId) && affectedSkillId && { affectedSkillId })
      };

      return [...characterTalents, newTalent];
    }
  }

  /**
   * Remove a talent purchase from a character's talent.
   * Updates the purchase count based on remaining purchases.
   * If no purchases remain, the talent is removed from the character.
   *
   * @param characterTalents - The character's current talents array
   * @param talentId - The string ID of the talent
   * @param advancementId - The advancement ID to remove
   * @returns Updated talents array with the purchase removed
   *
   * Requirements: 3.2
   */
  static removeTalentPurchase(
    characterTalents: Talent[],
    talentId: string,
    advancementId: string
  ): Talent[] {
    const talentIndex = characterTalents.findIndex((t) => t.id === talentId);
    if (talentIndex < 0) {
      return characterTalents;
    }

    const talent = characterTalents[talentIndex];
    const updatedHistory = (talent.purchaseHistory || []).filter(
      (p) => p.advancementId !== advancementId
    );

    // If no purchases remain, remove the talent entirely
    if (updatedHistory.length === 0) {
      return characterTalents.filter((_, index) => index !== talentIndex);
    }

    const updatedTalents = [...characterTalents];
    updatedTalents[talentIndex] = {
      ...talent,
      purchaseHistory: updatedHistory,
      purchaseCount: updatedHistory.length,
      // Update affectedSkillId if this is a talented talent
      ...(this.isTalentedTalent(talentId) && {
        affectedSkillId: updatedHistory[0]?.affectedSkillId
      })
    };

    return updatedTalents;
  }

  /**
   * Check if a skill already has the "Talented" bonus applied.
   * A skill can only have one "Talented" bonus.
   *
   * @param characterTalents - The character's talents array
   * @param skillId - The string ID of the skill to check
   * @returns True if the skill already has "Talented" applied
   *
   * Requirements: 4.3, 4.4
   */
  static skillHasTalentedBonus(characterTalents: Talent[], skillId: string): boolean {
    return characterTalents.some((talent) => {
      if (!this.isTalentedTalent(talent.id)) {
        return false;
      }

      // Check talent-level affectedSkillId
      if (talent.affectedSkillId === skillId) {
        return true;
      }

      // Check purchase history for affectedSkillId
      if (talent.purchaseHistory?.some((p) => p.affectedSkillId === skillId)) {
        return true;
      }

      // Check if the talent ID itself indicates the skill
      const extractedSkillId = this.extractSkillIdFromTalentedTalent(talent.id);
      if (extractedSkillId === skillId) {
        return true;
      }

      // Check specialization field (character creation path)
      if (talent.specialization) {
        const specId = talent.specialization
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
        if (specId === skillId) return true;
      }

      return false;
    });
  }

  /**
   * Get all skills that have the "Talented" bonus from a character's talents.
   *
   * @param characterTalents - The character's talents array
   * @returns Array of skill IDs that have "Talented" applied
   *
   * Requirements: 4.3
   */
  static getSkillsWithTalentedBonus(characterTalents: Talent[]): string[] {
    const skillIds: Set<string> = new Set();

    for (const talent of characterTalents) {
      if (!this.isTalentedTalent(talent.id)) {
        continue;
      }

      // Check talent-level affectedSkillId
      if (talent.affectedSkillId) {
        skillIds.add(talent.affectedSkillId);
      }

      // Check purchase history for affectedSkillId
      for (const purchase of talent.purchaseHistory || []) {
        if (purchase.affectedSkillId) {
          skillIds.add(purchase.affectedSkillId);
        }
      }

      // Check if the talent ID itself indicates the skill
      const extractedSkillId = this.extractSkillIdFromTalentedTalent(talent.id);
      if (extractedSkillId) {
        skillIds.add(extractedSkillId);
      }
    }

    return Array.from(skillIds);
  }

  /**
   * Calculate the "Talented" bonus for a specific skill.
   * Returns 10 if the skill has "Talented" applied, 0 otherwise.
   * A skill can only have one "Talented" bonus (max +10).
   *
   * @param characterTalents - The character's talents array
   * @param skillId - The string ID of the skill
   * @returns The bonus value (10 or 0)
   *
   * Requirements: 4.3, 9.4
   */
  static getTalentedBonusForSkill(characterTalents: Talent[], skillId: string): number {
    return this.skillHasTalentedBonus(characterTalents, skillId) ? 10 : 0;
  }

  /**
   * Check if a talent has a specific advancement purchase.
   *
   * @param talent - The talent to check
   * @param advancementId - The advancement ID to look for
   * @returns True if the talent has this advancement purchase
   */
  static hasAdvancementPurchase(talent: Talent, advancementId: string): boolean {
    return (talent.purchaseHistory || []).some((p) => p.advancementId === advancementId);
  }

  /**
   * Get available skills for "Talented" talent selection.
   * Excludes skills that already have "Talented" applied.
   *
   * @param characterTalents - The character's talents array
   * @param allSkillIds - Array of all available skill IDs
   * @returns Array of skill IDs that can receive "Talented"
   *
   * Requirements: 4.4, 4.5
   */
  static getAvailableSkillsForTalented(
    characterTalents: Talent[],
    allSkillIds: string[]
  ): string[] {
    const skillsWithTalented = this.getSkillsWithTalentedBonus(characterTalents);
    return allSkillIds.filter((skillId) => !skillsWithTalented.includes(skillId));
  }

  /**
   * Validate that a "Talented" purchase is valid.
   * Checks that the target skill doesn't already have "Talented".
   *
   * @param characterTalents - The character's talents array
   * @param targetSkillId - The skill ID to apply "Talented" to
   * @returns Object with valid flag and optional error message
   *
   * Requirements: 4.4
   */
  static validateTalentedPurchase(
    characterTalents: Talent[],
    targetSkillId: string
  ): { valid: boolean; error?: string } {
    if (this.skillHasTalentedBonus(characterTalents, targetSkillId)) {
      return {
        valid: false,
        error: `This skill already has the Talented talent applied`
      };
    }

    return { valid: true };
  }

  // ============================================================================
  // TALENT EFFECT CALCULATION METHODS
  // These methods calculate talent effects using ONLY character data.
  // No external data lookups are required - all information is self-contained.
  // ============================================================================

  /**
   * Calculate talent ranks from purchase history count.
   * Talent ranks = number of purchases in purchaseHistory[].
   *
   * This method uses ONLY character data - no external lookups required.
   *
   * @param talent - The talent to calculate ranks for
   * @returns The number of ranks (purchases) for this talent
   *
   * Requirements: 2.1, 2.2, 3.5, 9.3
   */
  static calculateTalentRanks(talent: Talent): number {
    // Talent ranks = count of purchases in purchaseHistory[]
    // This is the authoritative source - purchaseCount is derived from this
    return talent.purchaseHistory?.length ?? 0;
  }

  /**
   * Get all affected skill IDs for a "Talented" talent.
   * Collects skill IDs from:
   * 1. Talent-level affectedSkillId
   * 2. Each purchase's affectedSkillId in purchaseHistory
   * 3. Skill ID extracted from talent ID (e.g., "talented-commerce" -> "commerce")
   *
   * This method uses ONLY character data - no external lookups required.
   *
   * @param talent - The talent to get affected skills for
   * @returns Array of skill IDs that this talent affects (empty if not a "Talented" talent)
   *
   * Requirements: 2.1, 2.2, 4.3, 9.3
   */
  static getAffectedSkillIdsForTalent(talent: Talent): string[] {
    // Only "Talented" talents affect skills
    if (!this.isTalentedTalent(talent.id)) {
      return [];
    }

    const skillIds: Set<string> = new Set();

    // 1. Check talent-level affectedSkillId
    if (talent.affectedSkillId) {
      skillIds.add(talent.affectedSkillId);
    }

    // 2. Check each purchase in purchaseHistory for affectedSkillId
    for (const purchase of talent.purchaseHistory || []) {
      if (purchase.affectedSkillId) {
        skillIds.add(purchase.affectedSkillId);
      }
    }

    // 3. Check if the talent ID itself indicates the skill
    // e.g., "talented-commerce" -> "commerce"
    const extractedSkillId = this.extractSkillIdFromTalentedTalent(talent.id);
    if (extractedSkillId) {
      skillIds.add(extractedSkillId);
    }

    // 4. Check specialization field (set during character creation)
    // e.g., specialization: "Commerce" -> convert to skill ID "commerce"
    if (talent.specialization && skillIds.size === 0) {
      const specId = talent.specialization
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      if (specId) skillIds.add(specId);
    }

    return Array.from(skillIds);
  }

  /**
   * Get the total ranks for a specific talent by ID.
   * Convenience method that returns just the rank count.
   *
   * This method uses ONLY character data - no external lookups required.
   *
   * @param characterTalents - The character's talents array
   * @param talentId - The string ID of the talent
   * @returns The number of ranks (0 if talent not found)
   *
   * Requirements: 2.1, 2.2, 3.5, 9.3
   */
  static getTalentRanks(characterTalents: Talent[], talentId: string): number {
    const talent = characterTalents.find((t) => t.id === talentId);
    if (!talent) {
      return 0;
    }
    return this.calculateTalentRanks(talent);
  }

  /**
   * Check if a character has a specific talent with at least one rank.
   *
   * This method uses ONLY character data - no external lookups required.
   *
   * @param characterTalents - The character's talents array
   * @param talentId - The string ID of the talent
   * @returns True if the character has at least one rank in this talent
   *
   * Requirements: 2.1, 2.2, 9.3
   */
  static hasTalent(characterTalents: Talent[], talentId: string): boolean {
    return this.getTalentRanks(characterTalents, talentId) > 0;
  }
}
